import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Result,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MessageInstance } from 'antd/es/message/interface';
import {
  CheckCircleTwoTone,
  DownloadOutlined,
  EyeOutlined,
  ExclamationCircleTwoTone,
  SafetyCertificateOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useMemo } from 'react';

import { SEVERITY_COLORS } from '../constants.js';
import { copyToClipboard, downloadJSON, writeLocalBaseline } from '../utils.js';
import type { BaselineSnapshot, CookieIssue, HeaderIssue, ScanResult } from '../types.js';

type ScanResultCardProps = {
  result: ScanResult;
  baseline: BaselineSnapshot | null;
  onBaselineChange: (baseline: BaselineSnapshot) => void;
  messageApi: MessageInstance;
};

export function ScanResultCard({
  result,
  baseline,
  onBaselineChange,
  messageApi,
}: ScanResultCardProps) {
  const headerColumns: ColumnsType<HeaderIssue> = useMemo(
    () => [
      { title: 'Issue', dataIndex: 'title', key: 'title' },
      {
        title: 'Severity',
        dataIndex: 'severity',
        key: 'severity',
        width: 140,
        render: (severity: HeaderIssue['severity']) =>
          severity ? <Badge color={SEVERITY_COLORS[severity]} text={severity} /> : '—',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 90,
        render: (_: unknown, record: HeaderIssue) =>
          record.fixSnippet ? (
            <Tooltip title={`Copy: ${record.fixSnippet.label}`}>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={async () => {
                  if (record.fixSnippet?.content) {
                    await copyToClipboard(record.fixSnippet.content);
                    messageApi.success('Fix snippet copied');
                  }
                }}
              />
            </Tooltip>
          ) : (
            <span />
          ),
      },
    ],
    [messageApi],
  );

  const cookieColumns: ColumnsType<CookieIssue> = useMemo(
    () => [
      {
        title: 'Cookie',
        dataIndex: 'name',
        key: 'name',
        width: 200,
        render: (value: string) => <code>{value}</code>,
      },
      { title: 'Problem', dataIndex: 'problem', key: 'problem' },
      { title: 'Set by', dataIndex: 'setBy', key: 'setBy', width: 120 },
      {
        title: 'Flags',
        key: 'flags',
        render: (_: unknown, row) => (row.flags?.length ? row.flags.join(', ') : '—'),
        width: 200,
      },
      {
        title: 'Severity',
        key: 'severity',
        width: 120,
        render: (_: unknown, row) =>
          row.severity ? <Badge color={SEVERITY_COLORS[row.severity]} text={row.severity} /> : '—',
      },
    ],
    [],
  );

  const setAsBaseline = async () => {
    const baselineData: BaselineSnapshot = {
      id: 'local',
      headerIssues: result.headers.issues.length,
      cookieIssues: result.cookies.issues.length,
      new3p: result.thirdParty.newHosts.length,
      new3pHosts: result.thirdParty.newHosts,
    };
    writeLocalBaseline(result.finalUrl || result.url, result.gpc, baselineData);
    onBaselineChange(baselineData);
    messageApi.success('Current result set as baseline');
  };

  return (
    <Card title="Result">
      <Descriptions
        bordered
        size="small"
        column={1}
        extra={
          <Space>
            {result.status === 'ok' ? (
              <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>
                OK
              </Tag>
            ) : (
              <Tag color="red" icon={<ExclamationCircleTwoTone twoToneColor="#ff4d4f" />}>
                Issues
              </Tag>
            )}
            <Tag>{result.gpc ? 'GPC: On' : 'GPC: Off'}</Tag>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={async () => {
                await copyToClipboard(JSON.stringify(result.raw ?? result, null, 2));
                messageApi.success('JSON copied');
              }}
            >
              Copy JSON
            </Button>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadJSON('scan-result.json', result.raw ?? result)}
            >
              Download JSON
            </Button>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => window.open(result.finalUrl || result.url, '_blank')}
            >
              Open site
            </Button>
            <Button size="small" onClick={() => void setAsBaseline()}>
              Use as baseline
            </Button>
          </Space>
        }
      >
        <Descriptions.Item label="URL">
          <a href={result.url} target="_blank" rel="noreferrer">
            {result.url}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label="Final URL">
          <a href={result.finalUrl} target="_blank" rel="noreferrer">
            {result.finalUrl}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          {result.status === 'ok' ? (
            <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>
              OK
            </Tag>
          ) : (
            <Tag color="red" icon={<ExclamationCircleTwoTone twoToneColor="#ff4d4f" />}>
              Issues
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Duration">
          <Space>
            <span>{result.durationMs} ms</span>
          </Space>
        </Descriptions.Item>
      </Descriptions>

      {(result.artifacts?.screenshotUrl || result.artifacts?.traceUrl) && (
        <>
          <Divider />
          <Card size="small" title="Artifacts">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space wrap>
                {result.artifacts?.screenshotUrl && (
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => window.open(result.artifacts?.screenshotUrl || '#', '_blank')}
                  >
                    Download screenshot
                  </Button>
                )}
                {result.artifacts?.traceUrl && (
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => window.open(result.artifacts?.traceUrl || '#', '_blank')}
                  >
                    Download trace
                  </Button>
                )}
              </Space>
              {result.artifacts?.screenshotUrl && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={result.artifacts.screenshotUrl}
                    alt="Scan screenshot"
                    style={{
                      maxWidth: '100%',
                      width: '720px',
                      border: '1px solid #eee',
                      borderRadius: 6,
                    }}
                    referrerPolicy="no-referrer"
                  />
                </div>
              )}
            </Space>
          </Card>
        </>
      )}

      <Divider />
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Card
            title="Security headers"
            extra={<Tag icon={<SafetyCertificateOutlined />}>Headers</Tag>}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <strong>Present:</strong>{' '}
                {result.headers.present.length ? (
                  <Space wrap>
                    {result.headers.present.map((header) => (
                      <Tag key={header} color="blue">
                        {header}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <span>—</span>
                )}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <strong>Issues:</strong>
                {result.headers.issues.length === 0 ? (
                  <Alert
                    type="success"
                    message="No header issues"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                ) : (
                  <Table
                    size="small"
                    dataSource={result.headers.issues}
                    rowKey={(row) => row.code + row.title}
                    columns={headerColumns}
                    pagination={false}
                  />
                )}
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Cookie issues">
            {result.cookies.issues.length === 0 ? (
              <Alert type="success" message="No cookie issues detected" showIcon />
            ) : (
              <Table
                size="small"
                dataSource={result.cookies.issues}
                rowKey={(row) => row.name + row.problem}
                columns={cookieColumns}
                pagination={false}
              />
            )}
          </Card>
        </Col>

        {baseline && (
          <Col xs={24}>
            <Card title="Baseline vs Current (counts)">
              <Space size={12} wrap>
                <Tag
                  color={
                    (baseline.headerIssues || 0) > result.headers.issues.length
                      ? 'green'
                      : (baseline.headerIssues || 0) === result.headers.issues.length
                        ? 'default'
                        : 'red'
                  }
                >
                  Headers: {baseline.headerIssues} → {result.headers.issues.length}
                </Tag>
                <Tag
                  color={
                    (baseline.cookieIssues || 0) > result.cookies.issues.length
                      ? 'green'
                      : (baseline.cookieIssues || 0) === result.cookies.issues.length
                        ? 'default'
                        : 'red'
                  }
                >
                  Cookies: {baseline.cookieIssues} → {result.cookies.issues.length}
                </Tag>
                <Tag
                  color={
                    (baseline.new3p || 0) > result.thirdParty.newHosts.length
                      ? 'green'
                      : (baseline.new3p || 0) === result.thirdParty.newHosts.length
                        ? 'default'
                        : 'red'
                  }
                >
                  New 3P: {baseline.new3p} → {result.thirdParty.newHosts.length}
                </Tag>
              </Space>
              {baseline.new3pHosts && baseline.new3pHosts.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>New vs baseline:</strong>
                  <Space wrap style={{ marginLeft: 8 }}>
                    {result.thirdParty.newHosts
                      .filter((host) => !baseline?.new3pHosts?.includes(host))
                      .map((host) => (
                        <Tag key={host} color="volcano">
                          {host}
                        </Tag>
                      ))}
                  </Space>
                </div>
              )}
            </Card>
          </Col>
        )}

        <Col xs={24}>
          <Card title="New third‑party hosts (pre-consent)">
            {result.thirdParty.newHosts.length === 0 ? (
              <Result status="success" title="No new 3rd-party hosts detected" />
            ) : (
              <Space wrap>
                {result.thirdParty.newHosts.map((host) => (
                  <Tag key={host} color="volcano">
                    {host}
                  </Tag>
                ))}
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </Card>
  );
}
